import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { createSelector } from 'reselect';
import KeepAlive, { useActivate, useUnactivate } from 'react-activation';

import { logging, logPostInfo } from '../../../actions/log';
import { CONTEXT, WP_RECO_BANNER_65, WP_RECO_GROUP_65 } from '../../../config';
import CardIndex from '../../../card/CardIndex/CardIndex';
import Loading from '../../../components/Loading/Loading';
import ScrollTopWhite from '../../../components/ScrollTopWhite/ScrollTopWhite';
import Blank from '../Blank';
import styles from './WallpaperHotRank.module.less';
import { createAxios, logNative } from '../../../utils/utils';
import _LogInView from '../../../components/_LogInView/_LogInView';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

const selectUserInfo = createSelector(
  state => state.user,
  user => user.userInfo,
);

function WPRecommend({ pageType, source }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();
  const { action } = history;
  const pageContent = useRef();
  const logInfoData = useSelector(selectLogInfo);

  const [showNumber, setShowNumber] = useState(8);
  const [bannersData, setBannerData] = useState({});
  const [groupList, setGroupList] = useState(null);
  const userInfo = useSelector(selectUserInfo);

  const [initTime, setInitTime] = useState(+new Date());

  // 浏览时长打桩
  const leaveTimeLog = useCallback(() => {
    const leaveTime = +new Date();
    dispatch(
      logging({
        category: 'I',
        action: 'View_Time',
        label: '',
        param2: {
          source: source,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          initTime,
          leaveTime,
          time: leaveTime - initTime,
        },
      }),
    );
  }, [initTime]);

  const pagePvLog = useCallback(() => {
    dispatch(
      logging({
        category: 'Wallpaper',
        action: `P_${source}`,
        label: '',
        param2: {
          source: source,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
        },
      }),
    );
    dispatch(
      logPostInfo({
        source: source,
        source_p: logInfoData.source_p,
        source_pp: logInfoData.source_pp,
      }),
    );
  });

  useActivate(() => {
    setInitTime(+new Date());
  });
  useUnactivate(() => {
    leaveTimeLog();
  });

  useEffect(() => {
    pagePvLog();
    if (window.external && window.external.get_wallpaper_carousel_switch_info) {
      let switchData = JSON.parse(window.external.get_wallpaper_carousel_switch_info()) || {};
      dispatch(
        logging({
          category: 'Wallpaper',
          action: 'R_Wallpaper_Loop_Status',
          label: '',
          param2: {
            source: source,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            status: switchData.switch === 'on' ? 1 : 0,
          },
        }),
      );
    }
  }, []);
  useActivate(() => {
    pagePvLog();
  });

  useEffect(() => {
    createAxios().then(axios => {
      axios
        .get(WP_RECO_BANNER_65, {
          params: { pageType: pageType },
        })
        .then(res => {
          if (res.data.status === 0) {
            setBannerData(res.data.data);
          } else {
            setBannerData({});
          }
        })
        .catch(err => {
          setBannerData({});
          logNative(`获取壁纸页banner 错误${err}`, err);
        });
    });
    createAxios().then(axios => {
      axios
        .get(WP_RECO_GROUP_65, {
          params: { pageType: pageType },
        })
        .then(res => {
          if (res.data.status === 0) {
            setGroupList(res.data.data);
          } else {
            setGroupList([]);
          }
        })
        .catch(err => {
          setGroupList([]);
          logNative(`获取壁纸页卡片 错误${err}`, err);
        });
    });
  }, [userInfo]);

  // 监听浏览器大小变化自动适应
  useEffect(() => {
    window.addEventListener('resize', handleResize, false);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleResize = useCallback(e => {
    const clientWidth = (document.body && document.body.clientWidth) || 1060;
    if (clientWidth < 1330) {
      setShowNumber(8);
    } else if (clientWidth >= 1330 && clientWidth < 1465) {
      setShowNumber(10);
    } else if (clientWidth >= 1465 && clientWidth < 1600) {
      setShowNumber(11);
    } else if (clientWidth >= 1600 && clientWidth < 1870) {
      setShowNumber(13);
    } else if (clientWidth >= 1870) {
      setShowNumber(16);
    }
  }, []);

  if ((!bannersData || !bannersData.dataList || !groupList) && action !== 'POP') {
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );
  }

  const judgeLoading = () => {
    if (
      Object.keys(bannersData)?.length === 0 &&
      groupList?.length === 0 &&
      pathname !== `${CONTEXT}/wallpaper/wprecommend`
    ) {
      return true;
    } else {
      return false;
    }
  };

  const changView = data => {
    dispatch(
      logging({
        category: 'Wallpaper',
        action: 'I_Content_Show',
        label: '',
        param2: {
          source: source,
          rankCode: data?.rankCode,
          pageCardTitle: data?.pageCardTitle ?? '',
          pageCardId: data?.pageCardContentId ?? data?.pageCardId,
          source_p: logInfoData?.source_p,
          cardType: data?.cardType,
        },
      }),
    );
  };

  return (
    <div
      className={`${judgeLoading() ? styles.page_no_overflow : styles.page}`}
      ref={pageContent}
      pagescroll="PCStore_Page_Scroll"
    >
      <div className={styles.page_container}>
        <div>
          {bannersData?.dataList?.length > 0 && (
            <CardIndex
              data={bannersData}
              logInfo={{ tag: source, pageSource: 'recommend', changView: data => changView(data) }}
            />
          )}
          {groupList &&
            groupList.length > 0 &&
            groupList.map((item, i) => {
              return (
                <CardIndex
                  key={i}
                  data={{ ...item, node: pageContent.current }}
                  logInfo={{
                    tag: source,
                    pageSource: 'recommend',
                    changView: data => changView(data),
                  }}
                />
              );
            })}
        </div>

        <div className={styles.wp_scroll}>
          <ScrollTopWhite node={pageContent.current} quickly={true} />
        </div>

        {Object.keys(bannersData).length === 0 && groupList?.length === 0 && (
          <div className={styles.blank}>
            <Blank load={true} more />
          </div>
        )}
      </div>
    </div>
  );
}
// });

export default props => (
  <KeepAlive name="wallpaper">
    <WPRecommend {...props} />
  </KeepAlive>
);
